/**
 * EnrichedSchemaCard
 *
 * Beautiful, category-grouped summary of a service-request / listing's dynamic
 * schema data. Replaces the legacy flat "Details" grid.
 *
 * Inputs:
 *   - schema:   { fields: [{ key, label, type, category, options?, label_map? }] }
 *   - formData: { [key]: value }  — the actual data captured at submit time.
 *   - title:    optional header (default: "Details").
 *
 * Behaviour:
 *   - groups fields by their `category` taxonomy (temel, fiyat, yapi, mali, olanaklar, …)
 *   - hides empty / null / internal-id values
 *   - pretty-prints by field.type (number, boolean, select, multiselect, date, range)
 *   - falls back to title-case if a value can't be resolved against options
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Info, PoundSterling, Home, Wrench, Sparkles, FileText, Calendar } from 'lucide-react';

// Friendly group names (TR-schema-categories → English UI section titles + icons)
const GROUP_META = {
  temel:     { label: 'Basics',         icon: Info,          tone: 'emerald' },
  fiyat:     { label: 'Price',          icon: PoundSterling, tone: 'amber'   },
  yapi:      { label: 'Specifications', icon: Home,          tone: 'sky'     },
  mali:      { label: 'Financials',     icon: FileText,      tone: 'rose'    },
  olanaklar: { label: 'Features',       icon: Sparkles,      tone: 'violet'  },
  zaman:     { label: 'Schedule',       icon: Calendar,      tone: 'slate'   },
  other:     { label: 'Other',          icon: Wrench,        tone: 'slate'   },
};

const TONE_CLASSES = {
  emerald: 'bg-emerald-50 border-emerald-100 text-emerald-900',
  amber:   'bg-amber-50 border-amber-100 text-amber-900',
  sky:     'bg-sky-50 border-sky-100 text-sky-900',
  rose:    'bg-rose-50 border-rose-100 text-rose-900',
  violet:  'bg-violet-50 border-violet-100 text-violet-900',
  slate:   'bg-slate-50 border-slate-200 text-slate-800',
};

const titleCase = (s) =>
  String(s)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Currency-bearing field-key hints.
const PRICE_HINTS = ['price', 'rent', 'salary', 'fee', 'charge', 'deposit', 'ground_rent', 'ticket'];
const isPriceKey = (key) => PRICE_HINTS.some((p) => key.toLowerCase().includes(p));

const formatGBP = (n) => `£${new Intl.NumberFormat('en-GB').format(Number(n))}`;

const formatValue = (field, value) => {
  if (value === null || value === undefined || value === '') return null;
  const type = (field.type || '').toLowerCase();

  // boolean → Yes / No badge
  if (type === 'boolean' || typeof value === 'boolean') {
    return value === true || value === 'true' ? 'Yes' : 'No';
  }

  // Multi-select / array
  if (type === 'multiselect' || Array.isArray(value)) {
    const arr = Array.isArray(value) ? value : [value];
    if (arr.length === 0) return null;
    return arr.map((v) => titleCase(String(v))).join(', ');
  }

  // Select with predefined options
  if (type === 'select' && Array.isArray(field.options)) {
    const match = field.options.find((o) => String(o) === String(value));
    return match ? String(match) : titleCase(String(value));
  }

  // Number / currency
  if (type === 'number' || (typeof value !== 'string' && !isNaN(value))) {
    if (isPriceKey(field.key)) return formatGBP(value);
    return new Intl.NumberFormat('en-GB').format(Number(value));
  }

  // Date
  if (type === 'date') {
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    }
  }

  // Text / fallback
  return titleCase(String(value));
};

// Some keys must never be surfaced (internal ids, schema versioning).
const isInternalKey = (key) => {
  if (!key) return true;
  if (key === 'schema_id' || key === 'schema_type' || key === 'form_version') return true;
  if (key.endsWith('_id') || key.endsWith('_display')) return true;
  return false;
};

export const EnrichedSchemaCard = ({
  schema,
  formData,
  title = 'Details',
  'data-testid': dataTestId = 'enriched-schema-card',
}) => {
  if (!schema || !formData) return null;
  const fields = Array.isArray(schema.fields) ? schema.fields : [];
  if (fields.length === 0) return null;

  // Build category → [{field, value}] in schema order.
  const groups = new Map();
  for (const field of fields) {
    if (!field || !field.key || isInternalKey(field.key)) continue;
    const raw = formData[field.key];
    const formatted = formatValue(field, raw);
    if (formatted === null) continue;
    const cat = field.category && GROUP_META[field.category] ? field.category : 'other';
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat).push({ field, value: formatted });
  }

  if (groups.size === 0) return null;

  return (
    <Card className="border-slate-200" data-testid={dataTestId}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-slate-800">
          <Info className="w-4 h-4 text-emerald-600" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        {Array.from(groups.entries()).map(([cat, items]) => {
          const meta = GROUP_META[cat];
          const Icon = meta.icon;
          return (
            <div key={cat} data-testid={`schema-group-${cat}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${TONE_CLASSES[meta.tone]}`}>
                  <Icon className="w-3 h-3" />
                  {meta.label}
                </span>
                <span className="text-[11px] text-slate-400">{items.length} {items.length === 1 ? 'field' : 'fields'}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {items.map(({ field, value }) => {
                  const isBool = (field.type || '').toLowerCase() === 'boolean';
                  return (
                    <div
                      key={field.key}
                      className="bg-white border border-slate-200 rounded-lg px-3 py-2 hover:border-emerald-200 hover:bg-emerald-50/20 transition-colors"
                      data-testid={`schema-row-${field.key}`}
                    >
                      <div className="text-[11px] text-slate-500 truncate">{field.label || titleCase(field.key)}</div>
                      <div className="text-sm font-semibold text-slate-800 truncate flex items-center gap-1.5">
                        {isBool ? (
                          <Badge
                            variant="outline"
                            className={value === 'Yes' ? 'border-emerald-300 text-emerald-700 bg-emerald-50 text-xs' : 'border-slate-300 text-slate-600 text-xs'}
                          >
                            {value}
                          </Badge>
                        ) : (
                          value
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default EnrichedSchemaCard;
